import { useMemo, useSyncExternalStore } from "react";
import { useDesign, normalizeDesign } from "./store";
import { useLibrary } from "./library";
import {
  getSharedRoomBaseline,
  subscribeSharedRoomBaseline,
} from "./sharedRoomSync";
import type { Design } from "./types";

/**
 * Whether the open room differs from its last saved snapshot.
 *
 * - Shared room: compare against the sync session's last persisted
 *   design. Until that baseline exists, stay clean — the "has content"
 *   fallback would paint the dot for the whole time a shared room is open.
 * - Saved library room: compare against that room's snapshot.
 * - Neither: the empty default room is clean. Furniture, wall mounts, or
 *   openings mean the canvas was never saved.
 *
 * Comparison normalizes both sides and ignores key order, so backfilled
 * `model` / `material` / `frontKind` and Firestore field order are not
 * edits. Array order still counts (furniture z-order).
 *
 * Shared by the top-bar indicator and the rooms sheet.
 */
export function useHasUnsavedChanges(): boolean {
  const design = useDesign((s) => s.design);
  const activeSavedRoomId = useDesign((s) => s.activeSavedRoomId);
  const activeSharedRoomId = useDesign((s) => s.activeSharedRoomId);
  const rooms = useLibrary((s) => s.rooms);
  const sharedBaseline = useSyncExternalStore(
    subscribeSharedRoomBaseline,
    getSharedRoomBaseline,
    getServerSharedRoomBaseline
  );

  return useMemo(() => {
    if (activeSharedRoomId) {
      if (!sharedBaseline || sharedBaseline.roomId !== activeSharedRoomId) {
        return false;
      }
      return !designsEqual(design, sharedBaseline.design);
    }
    if (activeSavedRoomId) {
      const saved = rooms.find((r) => r.id === activeSavedRoomId);
      if (saved) {
        return !designsEqual(design, saved.design);
      }
      // Linked id no longer resolves (e.g. the saved room was deleted on
      // another device). Fall through to the unlinked heuristic so we
      // still flag in-progress work.
    }
    return (
      design.furniture.length > 0 ||
      design.mounts.length > 0 ||
      design.openings.length > 0
    );
  }, [design, activeSavedRoomId, activeSharedRoomId, rooms, sharedBaseline]);
}

function getServerSharedRoomBaseline() {
  return null;
}

/** Structural equality after the same backfill `loadDesign` applies. */
function designsEqual(a: Design, b: Design): boolean {
  return deepEqual(
    normalizeDesign(structuredClone(a)),
    normalizeDesign(structuredClone(b))
  );
}

function isNullish(value: unknown): boolean {
  return value === null || value === undefined;
}

/**
 * Key order is irrelevant. Missing, `null`, and `undefined` match so a
 * snapshot that never stored an optional field isn't an edit. Array
 * order is significant.
 */
function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (isNullish(a) || isNullish(b)) return isNullish(a) && isNullish(b);
  if (typeof a !== typeof b) return false;
  if (typeof a !== "object") return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) return false;
    }
    return true;
  }

  const aRec = a as Record<string, unknown>;
  const bRec = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(aRec), ...Object.keys(bRec)]);
  for (const key of keys) {
    if (!deepEqual(aRec[key], bRec[key])) return false;
  }
  return true;
}
